// UnsavedChangesModal — replaces window.confirm() for the dirty-draft-on-
// character-switch flow. Lists the specific fields that differ between the
// draft and the server record so the user knows what they're saving or
// discarding. Three actions:
//
//   Save to cloud   — sync the active character's draft to the server,
//                     then proceed with the pending switch on success.
//   Discard changes — drop the in-memory draft, then switch.
//   Cancel          — close the modal, stay on the current character.
//
// Blocks all other UI while open (full-screen overlay, z-index 100).
// See docs/UX_PRINCIPLES.md — User Control / Visibility of System Status.

import React, { useEffect, useMemo } from 'react'

// Compare draft vs server-side character. Returns a flat list of
// human-readable changes. Cheap, runs only when the modal opens.
function computeDiff(draft, server) {
  if (!draft || !server) return []
  const out = []

  if (draft.label !== server.label) {
    out.push({ field: 'Name', from: server.label || '(none)', to: draft.label || '(none)' })
  }
  if (draft.material?.kind !== server.material?.kind) {
    out.push({ field: 'Material kind', from: server.material?.kind ?? '?', to: draft.material?.kind ?? '?' })
  }
  if (draft.material?.color !== server.material?.color) {
    out.push({ field: 'Material color', from: server.material?.color ?? '?', to: draft.material?.color ?? '?' })
  }
  if (draft.rigPath !== server.rigPath) {
    const isBlob = typeof draft.rigPath === 'string' && draft.rigPath.startsWith('blob:')
    out.push({ field: 'Rig file', from: '(current)', to: isBlob ? '(new upload, not yet on server)' : (draft.rigPath ? '(new URL)' : '(cleared)') })
  }
  if (draft.faceMorphs !== server.faceMorphs) {
    out.push({ field: 'Face morphs', from: String(server.faceMorphs), to: String(draft.faceMorphs) })
  }

  // Texture / material slot changes (per role.channel).
  const draftMats = draft.materials ?? {}
  const serverMats = server.materials ?? {}
  for (const role of new Set([...Object.keys(draftMats), ...Object.keys(serverMats)])) {
    const d = draftMats[role] ?? {}
    const s = serverMats[role] ?? {}
    for (const key of new Set([...Object.keys(d), ...Object.keys(s)])) {
      if (d[key] !== s[key]) {
        const isBlob = typeof d[key] === 'string' && d[key].startsWith('blob:')
        const action = !s[key] ? 'added' : !d[key] ? 'removed' : isBlob ? 'replaced (pending upload)' : 'replaced'
        out.push({ field: `Texture ${role}.${key}`, from: '', to: action })
      }
    }
  }

  // Mesh-slot regex changes — show role names that changed without listing every regex.
  const draftSlots = draft.meshSlots ?? {}
  const serverSlots = server.meshSlots ?? {}
  for (const role of new Set([...Object.keys(draftSlots), ...Object.keys(serverSlots)])) {
    const d = JSON.stringify(draftSlots[role] ?? '')
    const s = JSON.stringify(serverSlots[role] ?? '')
    if (d !== s) {
      out.push({ field: `Mesh slot "${role}"`, from: '', to: 'rules modified' })
    }
  }

  return out
}

export default function UnsavedChangesModal({
  open,
  draft,
  server,
  pendingCharacterId,
  onChoice, // (choice: 'save' | 'discard' | 'cancel') => void
  busy = false, // true while a Save is in flight; disables buttons
}) {
  const diffs = useMemo(() => computeDiff(draft, server), [draft, server])

  // Escape cancels the modal — keyboard parity with the X/Cancel buttons.
  useEffect(() => {
    if (!open || busy) return
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onChoice('cancel')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, busy, onChoice])

  if (!open) return null

  const draftLabel = draft?.label || draft?.id || '(unknown)'
  const targetLabel = pendingCharacterId || '(unknown)'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-modal-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: '#000d', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        style={{
          width: 'min(560px, 100%)', maxHeight: '85vh', overflow: 'auto',
          background: '#16171a', border: '1px solid #ffffff1c',
          borderRadius: 'var(--m-radius)', boxShadow: 'var(--m-shadow-card)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '16px 20px 8px', borderBottom: '1px solid #ffffff0e' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: '#facc15', fontSize: 22, lineHeight: 1 }}>⚠</span>
            <div id="unsaved-modal-title" style={{ fontSize: 15, fontWeight: 600, color: '#ececef' }}>
              Unsaved changes for "{draftLabel}"
            </div>
          </div>
          <div style={{ fontFamily: 'var(--m-font)', fontSize: 11, color: '#8a8d97', marginTop: 6 }}>
            Switching to "{targetLabel}" — choose what to do with the unsaved work.
          </div>
        </div>

        <div style={{ padding: '14px 20px', flex: '0 1 auto', overflow: 'auto' }}>
          {diffs.length === 0 ? (
            <div style={{ fontFamily: 'var(--m-font)', fontSize: 12, color: '#8a8d97' }}>
              The draft is set but matches the server — nothing actually differs.
              You can safely Discard.
            </div>
          ) : (
            <>
              <div style={{ fontFamily: 'var(--m-font)', fontSize: 10, fontWeight: 700, letterSpacing: 'var(--m-head-ls)', textTransform: 'uppercase', color: '#6e7079', marginBottom: 8 }}>
                {diffs.length} unsaved change{diffs.length === 1 ? '' : 's'}
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {diffs.map((d, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'baseline', gap: 8,
                      fontFamily: 'var(--m-mono)', fontSize: 12, color: '#d4d4d8',
                      padding: '6px 10px', background: '#1c1d22', borderRadius: 4,
                      borderLeft: '2px solid #f59e0b',
                    }}
                  >
                    <span style={{ flex: '0 0 auto', color: '#fbbf24', fontWeight: 600 }}>{d.field}</span>
                    {d.from || d.to ? (
                      <span style={{ flex: 1, color: '#8a8d97' }}>
                        {d.from && d.to ? `${d.from} → ${d.to}` : (d.to || d.from)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div style={{
          padding: '14px 20px', borderTop: '1px solid #ffffff0e', background: '#1c1d22',
          display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap',
        }}>
          <button
            disabled={busy}
            onClick={() => onChoice('cancel')}
            style={btn('#a8aab2', '#2a2b30', '#3a3b42', busy)}
          >
            Cancel
          </button>
          <button
            disabled={busy}
            onClick={() => onChoice('discard')}
            style={btn('#f87171', '#2a1d1d', '#f87171', busy)}
          >
            Discard changes
          </button>
          <button
            disabled={busy}
            onClick={() => onChoice('save')}
            style={btn('#0f0f12', '#8b6cff', '#8b6cff', busy)}
          >
            {busy ? 'Saving…' : 'Save to cloud'}
          </button>
        </div>
      </div>
    </div>
  )
}

function btn(fg, bg, border, busy) {
  return {
    padding: '7px 16px',
    background: bg,
    color: fg,
    border: `1px solid ${border}`,
    borderRadius: 999,
    fontFamily: 'var(--m-font)',
    fontSize: 12,
    fontWeight: 700,
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.5 : 1,
    transition: 'opacity var(--m-ease)',
  }
}
